import { LegalClientEntity } from 'api/types/entity';
import { UpdateLegalClientParams } from 'api/types/params';
import { isFinanceProject, isGlobalProject } from 'config';
import { yesOrNot } from 'helpers/yesOrNo';
import {
  parseAdditionalDocumentFileIdsParam,
  parseAdditionalInformationFileIdsParam,
  parseCompanyTaxResidencyParam,
  parseConstitutionalDocumentParam,
  parseDocumentsFileIdsParam,
  parseLicenseParam,
  parseServiceContractParam,
  parseStateRegistrationParam,
  parseW8BENFormParam,
} from 'modules/client/common/helpers';

import { LegalClientFormType } from '../../forms/legalClientForm';

import { getActivityParam } from './getActivityParam';
import { getBankAccountsParam } from './getBankAccountsParam';
import { getBusinessRelationshipParam } from './getBusinessRelationshipParam';
import { getDebtsParam } from './getDebtsParam';
import { getFinanceFinancialMonitoringParam } from './getFinanceFinancialMonitoringParam';
import { getFinancingSourcesParam } from './getFinancingSourcesParam';
import { getGlobalFinancialMonitoringParam } from './getGlobalFinancialMonitoringParam';
import { getInvestmentPurposeAndNatureParam } from './getInvestmentPurposeAndNatureParam';
import { getKnowledgeAndExperienceSubmitData } from './getKnowledgeAndExperienceSubmitData';
import { getLegalClientAddressesParam } from './getLegalClientAddressesParam';
import { getOrganizationalAndLegalFormParam } from './getOrganizationalAndLegalFormParam';
import { getSourcesOfIncomeSubmitData } from './getSourcesOfIncomeSubmitData';
import { getTargetedFinancialInstrumentsParam } from './getTargetedFinancialInstrumentsParam';
import { getWithdrawalAccountsParam } from './getWithdrawalAccountsParam';

export const getLegalClientSubmitData = (
  legalClient: LegalClientEntity,
  values: LegalClientFormType
): UpdateLegalClientParams => {
  const isGlobal = isGlobalProject();
  const isFinance = isFinanceProject();

  const {
    SignaturesCard,
    Invoice,
    InvoicePaymentConfirmation,
    Questionnaire,
    SignedQuestionnaire,
    CompanySignedNoticeOfInvestmentAccountOpening,
    ClientServiceAgreement,
    CompanySignedClientServiceAgreement_Global,
    FullySignedClientServiceAgreement,
  } = parseDocumentsFileIdsParam(values.documentsFiles);

  return {
    versionNumber: legalClient.versionNumber!,
    reason: values.reasonChange.reason,
    fullName: values.companyName.fullName,
    shortName: values.companyName.shortName ?? null,
    fullNameEng: values.companyName.fullNameEng ?? null,
    shortNameEng: values.companyName.shortNameEng ?? null,
    activityTypes: values.activityTypes,
    OKPOCode: values.OKPOCode || null,

    bankAccounts: !isGlobal ? getBankAccountsParam(values) : undefined,

    debts: !isGlobal ? getDebtsParam(values, true) : undefined,

    financingSources: !isGlobal ? getFinancingSourcesParam(values) : undefined,

    orderSubmissionContacts: values.orderSubmissionContacts.map(
      ({ type, value, firstName, lastName, position, middleName }) => ({
        value,
        type: type.value,
        firstName,
        lastName,
        position,
        middleName,
      })
    ),

    ...getLegalClientAddressesParam(values),

    taxResidences: parseCompanyTaxResidencyParam(values),

    withdrawalAccounts: getWithdrawalAccountsParam(values),

    accreditedInvestorStatus:
      yesOrNot(values.statuses.accreditedInvestorStatus?.value) ?? null,

    riskLevel: values.riskLevel.riskLevel?.value ?? null,
    informationUpdateFrequency:
      values.riskLevel.informationUpdateFrequency?.value ?? null,
    riskLevelReviewDate: values.riskLevel.riskLevelReviewDate ?? null,
    lastInformationUpdateDate:
      values.riskLevel.lastInformationUpdateDate ?? null,
    lastOperationMonitoringResults:
      values.riskLevel.lastOperationMonitoringResults ?? null,
    usedServices: values.riskLevel.usedServices ?? null,

    code: values.accountCodes.code ?? null,
    centralDepositoryAccountNumber:
      values.accountCodes.centralDepositoryAccountNumber ?? null,
    centralDepositoryAccountNumberOpeningDate:
      values.accountCodes.centralDepositoryAccountNumberOpeningDate ?? null,
    personalAccountNumber: values.accountCodes.personalAccountNumber,
    personalAccountOpeningDate: values.accountCodes.personalAccountOpeningDate,
    managerId: values.accountCodes.manager?.id ?? null,
    personalAccountClosingDate:
      values.accountCodes.personalAccountClosingDate ?? null,
    personalAccountFreezingDate:
      values.accountCodes.personalAccountFreezingDate ?? null,

    businessRelationship: !isGlobal
      ? getBusinessRelationshipParam(values)
      : undefined,

    contacts: {
      email: values.companyContacts.email,
      phoneNumber: values.companyContacts.phone,
      isInformationDisclosureCompliant:
        yesOrNot(values.companyContacts.disclosureInformation?.value) ?? false,
      informationDisclosureCompliantResource:
        values.companyContacts.disclosureInformationResource,
      website: values.companyContacts.site,
    },

    license: parseLicenseParam(values),

    stateRegistration: parseStateRegistrationParam(values),

    registrationPlace: {
      country: values.registrationPlace.country?.id!,
      locality: values.registrationPlace.locality,
      specialRegistrationArea: values.registrationPlace.specialRegistrationArea,
    },

    organizationalAndLegalForm: getOrganizationalAndLegalFormParam(values),

    financialMonitoring: isFinance
      ? getFinanceFinancialMonitoringParam(values)
      : getGlobalFinancialMonitoringParam(values),

    openAccountOrder_Finance: isFinance
      ? {
          date: values.openAccountOrder_Finance.date || null,
          number: values.openAccountOrder_Finance.number || null,
          orderReceivedDate:
            values.openAccountOrder_Finance.orderReceivedDate || null,
          fileId: values.openAccountOrder_Finance.document?.[0]?.id || null,
        }
      : undefined,
    additionalInformationFileIds: parseAdditionalInformationFileIdsParam(
      values.additionalDocumentsFiles
    ),

    additionalDocumentFileIds: parseAdditionalDocumentFileIdsParam(
      values.documentsFiles
    ),

    constitutionalDocument: parseConstitutionalDocumentParam(
      values.constituentDocsFiles,
      values.constituentDocsDateOfLastChange
    ),

    category: values.general.category?.value || null,
    economicSector: values.general.economicSector?.value || null,
    classification: values.general.classification?.value || null,
    clientType: values.general.clientType?.value || null,
    isInsider: yesOrNot(values.general.isInsider?.value) ?? null,
    taxBenefits: values.general.taxBenefits || null,
    managementType: values.general.managementType?.value || null,
    isFinancialMonitoringSubject:
      yesOrNot(values.general.isFinancialMonitoringSubject?.value) ?? null,
    isKazakhstanResident:
      yesOrNot(values.general.isKazakhstanResident?.value) ?? null,

    serviceContract: parseServiceContractParam(values),

    tariff: values.general.tariff?.value,

    ownershipStructureFileIds: values.confirmStructure.files?.map((v) => v.id!),
    signaturesCardFileIds: SignaturesCard?.map((v) => v.id!),
    signedQuestionnaireFileIds: SignedQuestionnaire?.map((v) => v.id!),
    questionnaireFileId: Questionnaire?.[0]?.id,

    invoice: legalClient.invoice
      ? {
          fileId: Invoice?.[0]?.id,
          paymentConfirmationFileIds: InvoicePaymentConfirmation?.map(
            (v) => v.id!
          ),
        }
      : undefined,

    identificationNumbers: values.identificationNumbers,

    activity: isGlobal ? getActivityParam(values) : undefined,

    knowledgeAndExperience: isGlobal
      ? getKnowledgeAndExperienceSubmitData(values)
      : undefined,

    targetedFinancialInstruments: isGlobal
      ? getTargetedFinancialInstrumentsParam(values)
      : undefined,

    investmentPurposeAndNature: isGlobal
      ? getInvestmentPurposeAndNatureParam(values)
      : undefined,

    sourcesOfIncome: isGlobal
      ? getSourcesOfIncomeSubmitData(values)
      : undefined,

    W8BENForm: isGlobal ? parseW8BENFormParam(values) : undefined,

    companySignedNoticeOfInvestmentAccountOpeningFileId:
      CompanySignedNoticeOfInvestmentAccountOpening?.[0]?.id,
    blankClientServiceAgreementFileId_Global: isGlobal
      ? ClientServiceAgreement?.[0]?.id
      : undefined,
    companySignedClientServiceAgreementFileId_Global: isGlobal
      ? CompanySignedClientServiceAgreement_Global?.[0]?.id
      : undefined,
    fullySignedClientServiceAgreementFileId_Global: isGlobal
      ? FullySignedClientServiceAgreement?.[0]?.id
      : undefined,
  };
};
